import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  forwardRef,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core'
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms'

export interface ValidationOutput {
  invalid: boolean
}
@Component({
  selector: 'string-list-control',
  templateUrl: './string-list-control.component.html',
  styleUrls: ['./string-list-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StringListControlComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => StringListControlComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class StringListControlComponent implements ControlValueAccessor, Validator {
  itemList: string[] = []
  currentItem: string = ''
  @Input() delimiterCharacters: string[] = [' ', ',', ';', '\n'] // Default delimiters (Added newline as a delimiter)
  @Input() validationFunction: (item: string) => boolean
  @Input() normalizeFunction: (item: string) => string
  @Input() placeholderText = ''
  @Input() frozen: boolean = false
  @ViewChild('inputField') inputField: ElementRef<HTMLInputElement>
  @Output() validated = new EventEmitter<ValidationOutput>()

  // Function to validate items
  validateItem(item: string): boolean {
    if (this.validationFunction) {
      return this.validationFunction(item)
    }
    return true // Assume valid if no validation function provided
  }

  // Function to normalize items if a normalizeFunction has been provided
  normalizeItem(item: string): string {
    if (this.normalizeFunction) {
      return this.normalizeFunction(item)
    }
    return item // no normalization provided
  }

  // Register onChange function
  onChange: any = () => {}

  // Register onTouched function
  onTouched: any = () => {}

  // Write value to the component
  writeValue(value: string[]): void {
    if (Array.isArray(value)) {
      this.itemList = value
    }
  }

  // Register change function
  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  // Register touched function
  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  /**
   * Adds the current item(s) to the list.
   *
   * This function splits the current input value (`currentItem`) into multiple items
   * based on the defined delimiter characters (e.g., space, comma, semicolon, newline).
   * Each item is trimmed of whitespace and, if not empty, normalized and added to the list.
   * After adding the items to the list, it notifies Angular forms that the value has changed
   * and clears the current input value.
   */
  addItem() {
    // Split the current input value into multiple items using the defined delimiter characters.
    // The delimiter characters are joined into a single string, which is then used to create a
    // regular expression for splitting the input. This allows for splitting by any of the specified
    // delimiter characters (e.g., space, comma, semicolon, newline).
    const items = this.currentItem.split(new RegExp(`[${this.delimiterCharacters.join('')}]`))
    for (let item of items) {
      item = item.trim()
      if (item !== '') {
        this.itemList.push(this.normalizeItem(item))
      }
    }
    this.onChange(this.itemList) // Notify Angular forms that the value has changed
    this.currentItem = ''
  }

  // Remove an item from the list
  removeItem(index: number) {
    this.itemList.splice(index, 1)
    this.onChange(this.itemList)
  }

  // Handle key press to check for delimiter characters
  handleKeyDown(event: KeyboardEvent) {
    const delimiters = [...this.delimiterCharacters, 'Enter']
    const key = event.key
    if (delimiters.includes(key)) {
      this.addItem()
      event.preventDefault() // Prevent the delimiter character from being added to the input field
    }
    if (key === 'Backspace' && this.currentItem.length === 0 && this.itemList.length > 0) {
      // User hit backspace when there's nothing in the input field so they must be trying
      // to delete the most recently entered list entry
      this.removeItem(this.itemList.length - 1)
    }
  }

  /**
   * Handles the paste event for the input field.
   *
   * When a user pastes text into the input field, this function captures the
   * pasted content, appends it to the current input value, and processes it
   * by adding the items to the list.
   *
   * @param event - The ClipboardEvent triggered by the paste action.
   */
  handleClipboardPaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData || window['clipboardData']
    const pastedText = clipboardData.getData('text')
    this.currentItem += pastedText
    this.addItem()
    event.preventDefault()
  }
  focusInput(event: MouseEvent) {
    this.inputField.nativeElement.focus()
  }

  // Custom validator for the list
  validate(control: AbstractControl): { [key: string]: any } | null {
    if (!this.frozen && this.itemList.some((item) => !this.validateItem(item))) {
      const validated = { invalid: true }
      this.validated.emit(validated)
      return validated
    }
    this.validated.emit({ invalid: false })
    return null
  }
}
